/**
 * Ad-parameter extraction from a landing URL.
 *
 * Lives in @limo/shared because three surfaces need it: the viewer decides what
 * to capture, the Builder shows which ad a booking came from, and the edge
 * function parses gbraid/wbraid back out of bookings.landing_url at upload time.
 *
 * The edge function keeps its own literal copy
 * (`supabase/functions/send-to-google-ads/ad-params.ts`) because Deno cannot
 * import the workspace packages — the same constraint apply-quote-discount lives
 * with. CHANGE BOTH TOGETHER; one fixture list drives tests for each copy, so a
 * drift fails the suite instead of silently dropping a click ID.
 */

/** Every ad parameter we recognise on a landing URL. */
export interface AdParams {
  // Google click identifiers. Mutually exclusive in practice: gclid is the
  // normal one, gbraid/wbraid appear on iOS/Safari traffic where Apple's
  // tracking prevention blocks gclid.
  readonly gclid?: string
  readonly gbraid?: string
  readonly wbraid?: string
  readonly gad_source?: string
  readonly gclsrc?: string

  // Other platforms. Captured (they're in the landing URL regardless) but not
  // uploaded anywhere yet.
  readonly msclkid?: string
  readonly fbclid?: string

  readonly utm_source?: string
  readonly utm_medium?: string
  readonly utm_campaign?: string
  readonly utm_term?: string
  readonly utm_content?: string
}

/** The keys we read, in a stable order. */
export const AD_PARAM_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gclsrc',
  'msclkid',
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const satisfies readonly (keyof AdParams)[]

/** The Google click identifiers, in the priority order Google expects. */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const

// Google Ads tracking templates sometimes append the UTM tags AFTER a fragment,
// e.g. `/limo-service/?gclid=X#all-inclusive-rates?utm_source=google&utm_medium=cpc`.
// `new URL().searchParams` only sees the first query string, so those params are
// invisible to the obvious implementation. Splitting on both delimiters and
// parsing every trailing segment is what the qualifier learned to do
// (apps/ghl-qualifier/src/shared/attribution.ts) after losing real paid traffic
// to exactly this.
function readParam(url: string, key: string): string | undefined {
  const segments = url.split(/[?#]/).slice(1)
  for (const segment of segments) {
    const value = new URLSearchParams(segment).get(key)
    if (value && value.trim()) return value.trim()
  }
  return undefined
}

/**
 * Pull every recognised ad parameter out of a URL.
 *
 * Never throws: a malformed URL yields an empty object rather than breaking the
 * page it was called from.
 */
export function extractAdParams(url: string): AdParams {
  if (!url || typeof url !== 'string') return {}

  try {
    const params: Record<string, string> = {}
    for (const key of AD_PARAM_KEYS) {
      const value = readParam(url, key)
      if (value !== undefined) params[key] = value
    }
    return params as AdParams
  } catch {
    return {}
  }
}

/** True when the URL carries at least one recognised ad parameter. */
export function hasAdParams(params: AdParams): boolean {
  return AD_PARAM_KEYS.some((key) => Boolean(params[key]))
}

/**
 * The click identifier to send to Google, and which kind it is.
 *
 * Google treats the three as mutually exclusive — sending more than one is an
 * error — so this picks the first present in priority order.
 */
export function resolveClickId(
  params: AdParams
): { readonly type: (typeof CLICK_ID_KEYS)[number]; readonly value: string } | null {
  for (const type of CLICK_ID_KEYS) {
    const value = params[type]
    if (value) return { type, value }
  }
  return null
}
